import { useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import { cn } from "@krea/shared/utils";

import { LenderUserRoleEnum } from "@/features/common/enums";
import { useAssignLenderUserLenderApplicationMutation } from "@/features/lender-applications/hooks/data/use-assign-lender-user-lender-application.mutation";
import { useGetLenderCompanyUsers } from "@/features/users/hooks/use-get-lender-company-users";
import type { ILenderUser } from "@/features/users/types";

interface AssignLenderUserSelectProps {
  lenderApplicationId: number;
  assignedLenderUser: ILenderUser | null;
  onSuccess?: (
    data: ReturnType<
      typeof useAssignLenderUserLenderApplicationMutation
    >["data"],
  ) => void;
  className?: string;
}

export const AssignLenderUserSelect = ({
  lenderApplicationId,
  assignedLenderUser,
  onSuccess,
  className,
}: AssignLenderUserSelectProps) => {
  const { lenderCompanyId } = useParams();

  const { data: usersData } = useGetLenderCompanyUsers(Number(lenderCompanyId));

  const assignableUsers = useMemo(() => {
    return usersData?.filter(
      (user) => user.settings?.role !== LenderUserRoleEnum.DEVELOPER,
    );
  }, [usersData]);

  const { mutate: assignLenderUserLenderApplication, isPending } =
    useAssignLenderUserLenderApplicationMutation();

  const onAssignLenderUserLenderApplication = (value: string) => {
    const assignedLenderUserId = value === "null" ? null : Number(value);

    assignLenderUserLenderApplication(
      {
        lenderCompanyId: Number(lenderCompanyId),
        lenderApplicationId,
        data: { assignedLenderUserId },
      },
      {
        onSuccess: (response) => {
          onSuccess?.(response);
        },
      },
    );
  };

  return (
    <Select
      value={assignedLenderUser?.id.toString() ?? "null"}
      onValueChange={onAssignLenderUserLenderApplication}
      disabled={isPending}
    >
      <SelectTrigger className={cn("!flex", className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="null">No user assigned</SelectItem>
        {assignableUsers?.map((user) => (
          <SelectItem key={user.id} value={user.id.toString()}>
            {user.firstName} {user.lastName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
